import * as THREE from 'three';
import { LineBasicNodeMaterial } from 'three/webgpu';
import type { LayerBatch, LayerBatches } from './batch';

const THRESHOLD_DEG = 30;

// Feature edges of one geometry inside a batch, in the geometry's own frame.
function geometryEdges(batch: LayerBatch, geometryId: number): Float32Array {
  const range = batch.getGeometryRangeAt(geometryId)!;
  const src = batch.geometry;
  const position = src.getAttribute('position') as THREE.BufferAttribute;
  const index = src.getIndex()!;
  const g = new THREE.BufferGeometry();
  g.setAttribute(
    'position',
    new THREE.BufferAttribute(
      (position.array as Float32Array).slice(
        range.vertexStart * 3,
        (range.vertexStart + range.vertexCount) * 3,
      ),
      3,
    ),
  );
  const indices = new Uint32Array(range.indexCount);
  for (let i = 0; i < range.indexCount; i++)
    indices[i] = index.getX(range.indexStart + i) - range.vertexStart;
  g.setIndex(new THREE.BufferAttribute(indices, 1));
  const edges = new THREE.EdgesGeometry(g, THRESHOLD_DEG);
  const out = edges.getAttribute('position').array as Float32Array;
  g.dispose();
  edges.dispose();
  return out;
}

export function edgesMaterial(): LineBasicNodeMaterial {
  const m = new LineBasicNodeMaterial({ color: '#f0b429', transparent: true, opacity: 0.45 });
  m.depthWrite = false;
  return m;
}

// One LineSegments per storey with the feature edges of every instance, placed like the
// instances; parented to the layer's ClippingGroup so the build-up clip cuts them too.
export function layerEdges(layer: LayerBatches, material: THREE.Material): THREE.LineSegments[] {
  const perStorey = new Map<string, Float32Array[]>();
  const matrix = new THREE.Matrix4();
  const v = new THREE.Vector3();
  for (const batch of layer.batches) {
    const cache = new Map<number, Float32Array>();
    const chunks = perStorey.get(batch.storey) ?? [];
    perStorey.set(batch.storey, chunks);
    batch.expressIDs.forEach((_, id) => {
      const geometryId = batch.getGeometryIdAt(id);
      let local = cache.get(geometryId);
      if (!local) {
        local = geometryEdges(batch, geometryId);
        cache.set(geometryId, local);
      }
      batch.getMatrixAt(id, matrix).premultiply(batch.matrix);
      const placed = new Float32Array(local.length);
      for (let i = 0; i < local.length; i += 3) {
        v.fromArray(local, i).applyMatrix4(matrix).toArray(placed, i);
      }
      chunks.push(placed);
    });
  }
  return [...perStorey].map(([storey, chunks]) => {
    const positions = new Float32Array(chunks.reduce((n, c) => n + c.length, 0));
    let offset = 0;
    for (const c of chunks) {
      positions.set(c, offset);
      offset += c.length;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const lines = new THREE.LineSegments(geometry, material);
    lines.name = `${storey} edges`;
    lines.raycast = () => {};
    layer.root.add(lines);
    return lines;
  });
}
